import { useEffect, useMemo, useState } from "react";
import { Home } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import Reveal, { Caption } from "@/components/Reveal";
import ExcursionCard from "@/components/ExcursionCard";
import ExcursionBookModal from "@/components/ExcursionBookModal";

export default function ExcursionsPage() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("All");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/excursions").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const cats = useMemo(() => ["All", ...new Set(items.map((i) => i.category))], [items]);
  const filtered = cat === "All" ? items : items.filter((i) => i.category === cat);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 sm:py-24" data-testid="excursions-page">
      <Reveal>
        <Caption>{t("excursions.caption")}</Caption>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight mt-4 text-ink max-w-3xl">
          {t("excursions.titleA")} <span className="italic text-terracotta">{t("excursions.titleB")}</span>
        </h1>
        <p className="text-ink/60 max-w-2xl mt-6 leading-relaxed">{t("excursions.copy")}</p>
      </Reveal>

      <div className="flex flex-wrap gap-2 mt-10" data-testid="excursion-filters">
        {cats.map((c) => (
          <button
            key={c}
            data-testid={`filter-${c.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`}
            onClick={() => setCat(c)}
            className={`rounded-full px-5 py-2 text-sm font-medium border transition-colors ${
              cat === c ? "bg-night text-sand border-night" : "border-ink/15 hover:border-terracotta text-ink/70"
            }`}
          >
            {c === "All" ? t("excursions.all") : c}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="mt-16 text-ink/40 text-sm" data-testid="excursions-loading">{t("excursions.loading")}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-10" data-testid="excursions-grid">
          {filtered.map((e, i) => (
            <Reveal key={e.id} delay={(i % 3) * 0.08}>
              <ExcursionCard excursion={e} onBook={setBooking} />
            </Reveal>
          ))}
        </div>
      )}

      <Reveal className="mt-16 rounded-3xl bg-teal text-sand p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center gap-6">
        <div className="w-14 h-14 rounded-2xl bg-sand/10 flex items-center justify-center shrink-0">
          <Home size={24} className="text-gold" />
        </div>
        <div>
          <h3 className="font-serif text-2xl">{t("excursions.noteTitle")}</h3>
          <p className="text-sand/70 text-sm mt-2 leading-relaxed max-w-2xl">{t("excursions.noteCopy")}</p>
        </div>
      </Reveal>

      <ExcursionBookModal excursion={booking} open={!!booking} onClose={() => setBooking(null)} />
    </div>
  );
}
